import { workareaTheme } from "../helpers/workareaTheme";
import { useEffect, useMemo, useState } from "react";
import {
  ThemeProvider,
  Alert,
  Button,
  TextField,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CompactLeagueTable } from "./CompactLeagueTable";
import { LeagueSeasonComparison } from "./LeagueSeasonComparison";
import { LeagueWorkarea } from "./LeagueWorkarea";
import { renderAdminLeagueEditing } from "./AdminLeagueEditing";
import { OracleWorkspace } from "./OracleWorkspace";
import { FixtureSquads } from "./FixtureSquads";
import { fetchFixtureOddsRecords } from "./OddsTooltip";
import { formatFixtureResult } from "../helpers/fixtureResult";
import { getGermanFixtureDate } from "../helpers/fixtureDate";
import { formatFairOdd } from "../helpers/leagueFairMarkets";
import {
  ADMIN_TIP_PROFILE,
  fixtureTeams,
  matchdayBlocks,
  primaryLeagueTips,
  asOracleFixture,
  leagueTipTitle,
} from "../helpers/adminLeague";
import "./AdminLeagueWorkspace.css";


function Crest({ team }) {
  return team.image ? (
    <img
      src={team.image}
      alt=""
      onError={(e) => {
        e.currentTarget.style.visibility = "hidden";
      }}
    />
  ) : (
    <span className="al-crest-fallback">{team.name.slice(0, 2)}</span>
  );
}
export function Matchday({ block, previous, details, onSelect, showGoals }) {
  const { t } = useTranslation();
  if (!block)
    return <p>{t(`help.adminLeague.${previous ? "nonePrevious" : "noneNext"}`)}</p>;
  const merged = (f) => ({ ...f, ...details[f.id] });
  return (
    <section className="al-matchday">
      <div className="al-matchday-heading">
        <h2>{previous ? t("help.adminLeague.previous", { round: block.name }) : `Ausgewählter Spieltag ${block.name}`}</h2>

      </div>
      {!previous && <div className="al-matchday-label al-match-grid">
        <span className="al-pairing-label">Paarung</span>
        <span className="al-time-label">Anstoß</span>
        <span className="al-fair-label">betty fair – 1X2<span className="al-fair"><span>1</span><span>X</span><span>2</span></span></span>
      </div>}
      {block.fixtures.map((f) => {
        const d = merged(f);
        const [home, away] = fixtureTeams(d);
        const result = formatFixtureResult(d, "-");
        const [fullTime, halfTime] = result.split(" ");
        const kickoff = getGermanFixtureDate(f.starting_at);
        const noFair = !["homewin", "draw", "awaywin"].every((key) => Number(d.probs?.[key] ?? d[key]) > 0);

        return (
          <div key={f.id} className={`al-match al-match-grid ${previous ? "al-finished" : ""}`}>
            <button className="al-pairing" title="Paarung im Detail öffnen" aria-label={`${home.name} – ${away.name}: Paarung im Detail öffnen`} onClick={() => onSelect(f)}>
              <span className="al-team al-home" title={home.name}><Crest team={home} /><span>{home.name}</span></span>
              <span className="al-divider">–</span>
              <span className="al-team al-away" title={away.name}><Crest team={away} /><span>{away.name}</span></span>
            </button>
            {previous || result ? <><time>{kickoff?.toLocaleString("de-DE", { weekday: "short", hour: "2-digit", minute: "2-digit" }) || "–"}</time><span className="al-result"><b>{fullTime || "–"}</b>{halfTime && <small>{halfTime}</small>}</span></> : <>
              <time title={kickoff?.toLocaleString("de-DE")}>{kickoff?.toLocaleString("de-DE", { weekday: "short", hour: "2-digit", minute: "2-digit" }) || "–"}</time>
              <span className="al-fair" title={noFair ? "Keine betty Fair Odds · Compilation prüfen" : "betty Fair Odds"}>
                {["homewin", "draw", "awaywin"].map((key) => {
                  const p = Number(d.probs?.[key] ?? d[key]);
                  return <span key={key}>{formatFairOdd(p > 0 ? 1 / p : null)}</span>;
                })}
              </span>
            </>}
            {showGoals && !previous && <small className="al-goals">{t("help.adminLeague.goals")}: {d.goalexp?.home ?? "–"} - {d.goalexp?.away ?? "–"}</small>}
          </div>
        );
      })}
    </section>
  );
}
function Tips({ rows, onSelect }) {
  const { t } = useTranslation();
  const tr = (k) => t(`help.adminLeague.${k}`);
  const [profile, setProfile] = useState(ADMIN_TIP_PROFILE);
  const [filter, setFilter] = useState("all");
  const [quotes, setQuotes] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    Promise.all(
      rows.map(async (f) => [f.id, await fetchFixtureOddsRecords(f.id)])
    )
      .then((entries) => {
        if (active) setQuotes(Object.fromEntries(entries));
      })
      .catch((e) => active && setError(e.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [rows]);
  const tips = rows
    .flatMap((f) =>
      primaryLeagueTips(f, quotes[f.id], profile).map((tip) => ({
        ...tip,
        fixture: f,
      }))
    )
    .filter((tip) => filter === "all" || tip.market === filter);
  return (
    <>
      <div className="al-controls">
        <TextField
          size="small"
          select
          label={tr("market")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <MenuItem value="all">{tr("allMarkets")}</MenuItem>
          {profile.markets.map((m) => (
            <MenuItem key={m} value={m}>
              {tr(m)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={tr("basis")}
          value={profile.basis}
          onChange={(e) => setProfile({ ...profile, basis: e.target.value })}
        >
          {["average", "highest"].map((v) => (
            <MenuItem key={v} value={v}>
              {tr(v)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          label={tr("threshold")}
          value={profile.threshold}
          inputProps={{ min: 0, step: 0.5 }}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (Number.isFinite(value) && value >= 0)
              setProfile({ ...profile, threshold: value });
          }}
        />
      </div>
      <details className="al-help">
        <summary>{tr("details")}</summary>
        {tr("tipHelp")}
      </details>
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? (
        <p role="status">{tr("loading")}</p>
      ) : (
        <>
          <p>
            {t("help.adminLeague.tipCoverage", {
              count: Object.keys(quotes).length,
            })}
          </p>
          {!tips.length && <p>{tr("missingTips")}</p>}
          <div className="al-tips">
            {tips.map((tip) => {
              const [home, away] = fixtureTeams(tip.fixture);
              return (
                <article key={`${tip.fixtureId}:${tip.market}`}>
                  <h3 className="al-tip-title">{leagueTipTitle(tip)}</h3>
                  <small>{tr(tip.market)}</small>
                  <div className="al-tip-prices">Marktquote <b>{formatFairOdd(tip.quote)}</b> · betty fair <b>{formatFairOdd(tip.fair)}</b></div>
                  <header><Crest team={home} /><span>{home.name} – {away.name}</span><Crest team={away} /></header>
                  <small>{tr("advantage")} +{tip.advantage.toFixed(1)} %</small>
                  <Button onClick={() => onSelect(tip.fixture)}>
                    {tr("details")}
                  </Button>
                </article>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
export function AdminLeagueWorkspace({
  user,
  league,
  season,
  seasons,
  round,
  rounds,
  fixtures,
  rows,
  comparison,
  base,
  teams,
  controls,
  pairings,
  fixtureWorkspace,
  selectedFixture,
  onSelect,
  onRoundChange,
  onDirtyChange,
  onSaved,
  dirty,
  onRefreshResults,
}) {
  const { t } = useTranslation();
  const tr = (k) => t(`help.adminLeague.${k}`);
  const [open, setOpen] = useState({});
  const [showGoals, setShowGoals] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const [checkedAt, setCheckedAt] = useState(null);
  useEffect(() => {
    if (!onRefreshResults) return;
    let active = true, busy = false;
    const refresh = async () => {
      if (busy || document.hidden) return;
      busy = true;
      try { await onRefreshResults(() => active); if (active) { setRefreshError(""); setCheckedAt(new Date()); } }
      catch (e) { if (active) setRefreshError(e.message); }
      finally { busy = false; }
    };
    refresh();
    const timer = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => { active = false; clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [onRefreshResults]);
  const blocks = useMemo(
    () => matchdayBlocks(fixtures, rounds, round?.id),
    [fixtures, rounds, round?.id]
  );
  const logos = useMemo(
    () =>
      new Map(
        fixtures.flatMap((f) =>
          (f.participants || []).map((team) => [
            Number(team.id),
            team.image_path,
          ])
        )
      ),
    [fixtures]
  );
  // Fixture data and selection belong to FixturesOverview, including enrichment.
  const details = useMemo(() => Object.fromEntries(rows.map(row => [row.id, row])), [rows]);
  const contextKey = `${league?.id}:${season?.id}:${round?.id}`;
  const oracleFixtures = useMemo(
    () => rows.map((f) => asOracleFixture(f, league)),
    [rows, league]
  );
  const select = (f) => {
    if (dirty) return;
    const target = rounds.find(
      (r) => Number(r.id) === Number(f.round_id ?? f.round?.id)
    );
    if (target && Number(target.id) !== Number(round?.id))
      onRoundChange(target, f.id);
    else onSelect(f.id);
    setOpen((s) => ({ ...s, fixture: true }));
  };
  const panel = (key, content) => (
    <details
      key={key}
      id={`al-section-${key}`}
      className="al-section"
      open={!!open[key]}
      onToggle={(e) => {
        const value = e.currentTarget.open;
        setOpen((s) => (s[key] === value ? s : { ...s, [key]: value }));
      }}
    >
      <summary>{tr(key)}</summary>
      <div className="al-content">{content}</div>
    </details>
  );
  useEffect(() => {
    if (open.fixture && selectedFixture?.id) document.getElementById("al-section-fixture")?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [open.fixture, selectedFixture?.id]);
  const selectedTeams = selectedFixture ? fixtureTeams(selectedFixture) : [];
  return (
    <ThemeProvider theme={workareaTheme}>
      <main className="al-workspace" data-testid="admin-league-workspace">
        <div className="al-title">
          <b>
            {league?.name} · {season?.name}
          </b>
          <small>{tr("testLimit")}</small>
        </div>
        {controls}
        <small>Ergebnisabgleich alle 30 Sekunden{checkedAt ? ` · zuletzt ${checkedAt.toLocaleTimeString("de-DE")}` : ""}</small>
        {refreshError && <Alert severity="warning">{refreshError}</Alert>}
        <div className="al-overview">
          <div className="al-rounds">
            <Matchday
              block={blocks.previous}
              previous
              details={details}
              onSelect={select}
            />
            <Matchday
              block={blocks.next}
              details={details}
              onSelect={select}
              showGoals={showGoals}
            />
            <label className="al-help">
              <input
                type="checkbox"
                checked={showGoals}
                onChange={(e) => setShowGoals(e.target.checked)}
              />
              {tr("goals")}
            </label>
          </div>
          <div className="al-standings">
            <CompactLeagueTable
              seasonId={season?.id}
              actualRows={base?.actual_table || []}
              teamLogos={logos}
              standingRules={base?.standing_rules?.items || []}
              transitionItems={base?.team_transitions?.items || []}
              footballOnly
            />
          </div>
        </div>
        <section className="al-matchday-work" data-testid="league-matchday-work">
          <h2>Spieltags- &amp; Ligastatistiken</h2>
          <details className="al-section"><summary>Ligastatistiken · gesamte Saison</summary><div className="al-content"><LeagueSeasonComparison data={comparison} /></div></details>
          {!base?.actual_table?.length && <p role="status">Keine gespeicherte klassische Ligatabelle verfügbar. Bei Gruppen- und Pokalwettbewerben gelten die jeweiligen Wettbewerbsphasen.</p>}
          <h2>Spieltag arbeiten · {round?.name || "–"}</h2>
          {pairings}
        </section>
        {season && (
          <LeagueWorkarea
            key={`${league.id}:${season.id}`}
            user={user}
            league={league}
            season={season}
            seasons={seasons}
            teams={teams}
            comparison={comparison}
            base={base}
            onSaved={onSaved}
            onDirtyChange={onDirtyChange}
            teamLogos={logos}
            renderWorkspaceContent={renderAdminLeagueEditing}
          />
        )}
        {panel("tips", open.tips && <Tips key={contextKey} rows={rows} onSelect={select} />)}
        {panel(
          "oracle",
          open.oracle && (
            <OracleWorkspace
              key={`${league?.id}:${season?.id}:${round?.id}`}
              user={user}
              leagueFixtures={oracleFixtures}
            />
          )
        )}
        {panel(
          "fixture",
          <>
            <p role="status">Paarung im Detail: {selectedTeams.map(team => team.name).join(" – ")}</p>
            <TextField
              size="small"
              select
              label={tr("selectFixture")}
              value={selectedFixture?.id || ""}
              disabled={dirty}
              onChange={(e) => onSelect(Number(e.target.value))}
            >
              {rows.map((f) => {
                const [a, b] = fixtureTeams(f);
                return (
                  <MenuItem key={f.id} value={f.id}>
                    {a.name} – {b.name}
                  </MenuItem>
                );
              })}
            </TextField>
            {fixtureWorkspace}
          </>
        )}
        {panel(
          "squads",
          open.squads && selectedFixture && (
            <FixtureSquads
              key={selectedFixture.id}
              open
              seasonId={season?.id}
              fixtureId={selectedFixture.id}
              teams={selectedTeams}
            />
          )
        )}
      </main>
    </ThemeProvider>
  );
}
